/**
 * Central, pure scoring rules for the game. Single source of truth for every
 * point value and multiplier so the level scenes and the unit tests agree. No
 * Phaser imports here on purpose: this module is plain math and is unit-tested
 * with vitest.
 */

/** Fallback base value for a collectible whose kind has no explicit entry. */
export const DEFAULT_COLLECTIBLE_BASE = 10;

/** Per-kind base point values. Punchy ~5x spread (money 50 vs laptop 10).
 *  Tunable. An explicit Tiled `value` prop still overrides these. */
export const COLLECTIBLE_BASE: Record<string, number> = {
  laptop: 10,
  document: 10,
  feature: 15,
  idea: 15,
  lightbulb: 15,
  vw: 25,
  bionic: 30,
  money: 50,
};

/** Base value for a collectible of `kind`, or the default if unknown. */
export function baseValueForKind(kind: string): number {
  return COLLECTIBLE_BASE[kind] ?? DEFAULT_COLLECTIBLE_BASE;
}

/** Multiplier for a single (non-combo) thrown collect or kill. */
export const THROW_MULTIPLIER = 2;

/** Points for finding the hidden Sheepy (one per level). A rare exploration
 *  reward, worth more than the top collectible. Tunable. */
export const SHEEPY_BONUS = 100;

/** Base kill values by enemy size. */
export const ENEMY_BASE = { small: 20, big: 40 } as const;

/** count>=2 -> combo (xcount); count===1 -> single throw (xTHROW_MULTIPLIER). */
export function throwMultiplier(hitCount: number): number {
  return hitCount >= 2 ? hitCount : THROW_MULTIPLIER;
}

export interface SpeedTier {
  maxSeconds: number;
  bonus: number;
}

/** Fixed, tunable tiers (~10-25% of a full clear at the top band). Level 3
 *  (andrew) is intentionally absent so it awards no speed bonus. */
export const SPEED_TIERS: Record<string, SpeedTier[]> = {
  dan: [
    { maxSeconds: 45, bonus: 150 },
    { maxSeconds: 75, bonus: 75 },
    { maxSeconds: 120, bonus: 25 },
  ],
  lucas: [
    { maxSeconds: 70, bonus: 150 },
    { maxSeconds: 110, bonus: 75 },
    { maxSeconds: 150, bonus: 25 },
  ],
};

/** Bonus for finishing `levelKey` in `elapsedSeconds`; 0 if no tier matches or
 *  the level has no tiers. */
export function speedBonus(levelKey: string, elapsedSeconds: number): number {
  const tiers = SPEED_TIERS[levelKey];
  if (!tiers) return 0;
  for (const t of tiers) if (elapsedSeconds <= t.maxSeconds) return t.bonus;
  return 0;
}
